import type {RevaLanguage} from "@reva/shared/i18n";
import type {
    ComputeOriginalValue,
    FormatCell,
    IndicationSpec,
    ValueIndicationDataRow,
} from "@reva/shared/indication-table";
import {
    aggregateIndicationTableData as sharedAggregateIndicationTableData,
    processIndicationRowGroups as sharedProcessIndicationRowGroups,
} from "@reva/shared/indication-table";
import {formatTypedValue, formatValueRange, parseGuiType} from "@reva/shared/number-format";
import type {
    RevaIndicationEndpoint,
    RevaSpec,
    RevaSpecOutputCategory,
    RevaSpecOutputRowGroup,
} from "@reva/shared/spec-conversion";
import {indexRangesByApiParam} from "@reva/shared/spec-conversion";

import type {IndicationParameterDetails} from "../redux/valueIndicationSlice";
import {formatNonRoundedValue} from "./numberFormat";

function toSharedSpec(spec: RevaSpec, endpoint: RevaIndicationEndpoint): IndicationSpec {
    const pageSpec = spec.pages[endpoint];
    return {pages: pageSpec ? {[endpoint]: pageSpec} : {}} as IndicationSpec;
}

// ---------------------------------------------------------------------------
// Formatting callbacks injected into the shared aggregation logic
// ---------------------------------------------------------------------------

function createFormatCell(rangesByApiParam: ReturnType<typeof indexRangesByApiParam>): FormatCell {
    return (lang, value, cell, omitUnit) => {
        const apiParam = cell.field_is_input ? cell.field : cell.associatedInputApiParam;
        const rangeLabel = apiParam ? formatValueRange(lang, value, rangesByApiParam[apiParam]) : null;
        if (rangeLabel != null) return rangeLabel;

        const categories = cell.categories as RevaSpecOutputCategory[] | undefined;
        const guiType = parseGuiType(
            cell.format,
            omitUnit ? null : cell.unitDe,
            omitUnit ? null : cell.unitEn,
            categories,
        );
        return formatTypedValue(lang, value, guiType);
    };
}

const computeOriginalValue: ComputeOriginalValue = (cell, renderedValue, lang, indicationDetails) => {
    const value = indicationDetails.output[cell.field];
    if (value == null) return null;
    const nonRoundedValue = formatNonRoundedValue(value as number, lang);
    if (renderedValue.split(" ")[0].length < nonRoundedValue.length) {
        return nonRoundedValue;
    }
    return null;
};

/**
 * Compute and aggregate the data for indication details to the row-data to display it as a table.
 */
export function aggregateIndicationTableData(
    lang: RevaLanguage,
    spec: RevaSpec | null | undefined,
    endpoint: RevaIndicationEndpoint,
    indicationDetails: IndicationParameterDetails,
): ValueIndicationDataRow[] {
    return sharedAggregateIndicationTableData(
        lang,
        spec ? toSharedSpec(spec, endpoint) : spec,
        endpoint,
        indicationDetails,
        createFormatCell(indexRangesByApiParam(spec)),
        computeOriginalValue,
    );
}

/**
 * Process multiple row groups for the indication table data.
 */
export function processIndicationRowGroups(
    lang: RevaLanguage,
    spec: RevaSpec,
    endpoint: RevaIndicationEndpoint,
    indicationDetails: IndicationParameterDetails,
    groups: RevaSpecOutputRowGroup[],
): ValueIndicationDataRow[] {
    return sharedProcessIndicationRowGroups(
        lang,
        toSharedSpec(spec, endpoint),
        endpoint,
        indicationDetails,
        groups,
        createFormatCell(indexRangesByApiParam(spec)),
        computeOriginalValue,
    );
}
